import { ethers } from "ethers";
import { supabase } from "./supabase";

// Smart Contract của GiacNgoCertificate
const CONTRACT_ADDRESS = "0x948ab30E7d703FB830D59B14Bf644E53669f58B2";
const POLYGON_RPC_URL = "https://polygon-rpc.com";
const POLYGON_CHAIN_ID = 137; // Polygon Mainnet

// ABI (Application Binary Interface) của smart contract
// Đây là phần quan trọng để tương tác với smart contract
const CONTRACT_ABI = [
  // Mint Certificate - tạo chứng chỉ mới
  "function mintCertificate(address recipient, string calldata certificateId, string calldata courseId, string calldata courseName, string calldata recipientName, string calldata metadataURI) external returns (uint256)",

  // Get Certificate Info - lấy thông tin chi tiết của chứng chỉ
  "function getCertificateInfo(uint256 tokenId) external view returns (string memory certificateId, string memory courseId, string memory courseName, string memory recipientName, uint256 issueDate, string memory metadataURI)",

  // Verify Ownership - xác minh quyền sở hữu
  "function verifyOwnership(address owner, string calldata certificateId) external view returns (bool)",

  // Has Certificate - kiểm tra người dùng đã có chứng chỉ chưa
  "function hasCertificate(address owner, string calldata courseId) external view returns (bool)",

  // Get Token ID By Certificate ID - lấy token ID từ certificate ID
  "function getTokenIdByCertificateId(string calldata certificateId) external view returns (uint256)",

  // Events
  "event CertificateIssued(uint256 indexed tokenId, address indexed recipient, string certificateId, string courseId)",
];

/**
 * Kết nối với provider và trả về đối tượng provider
 */
export const getProvider = () => {
  return new ethers.providers.JsonRpcProvider(POLYGON_RPC_URL);
};

/**
 * Kiểm tra nếu MetaMask được cài đặt
 */
export const checkIfMetaMaskInstalled = () => {
  if (typeof window === "undefined") return false;

  return window.ethereum !== undefined && window.ethereum.isMetaMask === true;
};

/**
 * Kiểm tra xem ví đã được kết nối chưa
 */
export const checkIfWalletConnected = async () => {
  try {
    // Kiểm tra nếu MetaMask được cài đặt
    if (!checkIfMetaMaskInstalled()) {
      console.log("MetaMask is not installed");
      return { connected: false, address: "" };
    }

    // Lấy các tài khoản đã kết nối
    const accounts = await window.ethereum.request({ method: "eth_accounts" });

    // Kiểm tra nếu có tài khoản đã kết nối
    const connected = Array.isArray(accounts) && accounts.length > 0;
    const address = connected ? accounts[0] : "";

    // Log kết quả
    console.log("Wallet connection check:", { connected, address });

    return { connected, address };
  } catch (error) {
    console.error("Error checking wallet connection:", error);
    return { connected: false, address: "" };
  }
};

/**
 * Kết nối với MetaMask và trả về đối tượng signer
 */
export const connectWallet = async () => {
  try {
    if (!checkIfMetaMaskInstalled()) {
      throw new Error("MetaMask chưa được cài đặt");
    }

    console.log("Requesting wallet connection...");

    // Yêu cầu kết nối đến MetaMask
    const accounts = await window.ethereum.request({
      method: "eth_requestAccounts",
    });

    if (!accounts || accounts.length === 0) {
      throw new Error("Không thể kết nối đến ví");
    }

    const address = accounts[0];
    console.log("Connected to wallet:", address);

    // Kiểm tra chain ID để đảm bảo đang ở trên mạng Polygon
    const chainId = await window.ethereum.request({ method: "eth_chainId" });

    // Polygon Mainnet Chain ID: 0x89 (137)
    // Polygon Mumbai Testnet Chain ID: 0x13881 (80001)
    const polygonChainId = "0x89"; // Mainnet
    const mumbaiChainId = "0x13881"; // Testnet

    const isCorrectNetwork =
      chainId === polygonChainId || chainId === mumbaiChainId;

    if (!isCorrectNetwork) {
      console.log("Not on Polygon network. Current chain ID:", chainId);
      // Yêu cầu người dùng chuyển sang mạng Polygon
      try {
        await window.ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: polygonChainId }],
        });
        console.log("Switched to Polygon network");
      } catch (switchError) {
        // Lỗi 4902 có nghĩa là mạng chưa được thêm vào MetaMask
        if (switchError.code === 4902) {
          try {
            await window.ethereum.request({
              method: "wallet_addEthereumChain",
              params: [
                {
                  chainId: polygonChainId,
                  chainName: "Polygon Mainnet",
                  nativeCurrency: {
                    name: "MATIC",
                    symbol: "MATIC",
                    decimals: 18,
                  },
                  rpcUrls: ["https://polygon-rpc.com/"],
                  blockExplorerUrls: ["https://polygonscan.com/"],
                },
              ],
            });
            console.log("Added Polygon network to MetaMask");
          } catch (addError) {
            console.error("Failed to add network:", addError);
            throw new Error(
              "Không thể thêm mạng Polygon. Vui lòng thử lại sau.",
            );
          }
        } else {
          console.error("Failed to switch network:", switchError);
          throw new Error(
            "Không thể chuyển sang mạng Polygon. Vui lòng thử thủ công.",
          );
        }
      }
    }

    return { address };
  } catch (error) {
    console.error("Error connecting wallet:", error);
    throw error;
  }
};

/**
 * Tạo metadata cho NFT Certificate theo chuẩn OpenSea
 */
export const createCertificateMetadata = (certificateData) => {
  return {
    name: `Giác Ngộ Certificate - ${certificateData.courseName}`,
    description: `Certificate of completion for ${certificateData.courseName} issued to ${certificateData.recipientName}`,
    image: certificateData.imageUrl,
    external_url: `https://giacngo.org/certificate/${certificateData.certificateId}`,
    attributes: [
      {
        trait_type: "Course",
        value: certificateData.courseName,
      },
      {
        trait_type: "Recipient",
        value: certificateData.recipientName,
      },
      {
        trait_type: "Issue Date",
        value: new Date().toISOString().split("T")[0],
      },
      {
        trait_type: "Certificate ID",
        value: certificateData.certificateId,
      },
    ],
  };
};

/**
 * Upload hình ảnh certificate lên Cloudflare
 */
export const uploadCertificateImage = async (
  imageBlob: Blob | string,
  certificateId: string,
): Promise<any> => {
  try {
    // Lấy token authentication từ Supabase
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      throw new Error("Bạn chưa đăng nhập hoặc phiên đăng nhập đã hết hạn");
    }

    // Chuyển đổi Blob thành base64 nếu cần
    let imageBase64: string;

    if (imageBlob instanceof Blob) {
      // Chuyển Blob thành base64
      imageBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(imageBlob);
      });
    } else {
      // Nếu đã là string thì giữ nguyên
      imageBase64 = imageBlob;
    }

    console.log(
      "Đã chuyển đổi ảnh sang base64, kích thước:",
      imageBase64.length,
    );

    // Gửi request để upload ảnh
    const response = await fetch("/api/certificates/upload-image", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        certificateId,
        image: imageBase64,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Lỗi khi upload ảnh: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result.imageUrl;
  } catch (error) {
    console.error("Lỗi khi upload ảnh certificate:", error);
    throw error;
  }
};

/**
 * Lưu metadata của NFT certificate
 */
export const saveNFTMetadata = async (metadata: any): Promise<string> => {
  try {
    // Lấy token authentication từ Supabase
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      throw new Error("Bạn chưa đăng nhập hoặc phiên đăng nhập đã hết hạn");
    }

    // Gửi request để lưu metadata
    // Đảm bảo gửi đúng cấu trúc mà API mong đợi (bọc trong property metadata)
    const response = await fetch("/api/certificates/save-metadata", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ metadata }), // Bọc metadata trong một object
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Lỗi khi lưu metadata: ${response.status} - ${errorText}`,
      );
    }

    const result = await response.json();
    return result.metadataUrl; // Đảm bảo trường này trùng với response từ API
  } catch (error) {
    console.error("Lỗi khi lưu metadata:", error);
    throw error;
  }
};

/**
 * Cập nhật thông tin NFT vào certificate
 */
export const updateCertificateWithNFT = async (
  certificateId: string,
  tokenId: string,
  transactionHash: string,
  isExisting: boolean = false,
  blockchainVerificationUrl?: string,
): Promise<boolean> => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return false;

    // Tạo URL xác minh từ transactionHash (nếu không được cung cấp trực tiếp)
    const verificationUrl =
      blockchainVerificationUrl ||
      (transactionHash && transactionHash !== "manual_verified"
        ? `https://polygonscan.com/tx/${transactionHash}`
        : "https://giacngo.org/verified");

    // Cập nhật certificate với thông tin NFT
    const { data, error } = await supabase
      .from("certificates")
      .update({
        nft_token_id: tokenId,
        nft_transaction_hash:
          transactionHash !== "manual_verified" ? transactionHash : null,
        blockchain_verification_url: verificationUrl,
        is_nft: true,
        updated_at: new Date().toISOString(),
      })
      .eq("certificate_id", certificateId)
      .select()
      .single();

    if (error) {
      console.error("Error updating certificate with NFT:", error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error("Error in updateCertificateWithNFT:", error);
    return false;
  }
};

/**
 * Mint certificate NFT mới
 */
export const mintNFTCertificate = async (
  certificateId,
  courseId,
  courseName,
  recipientName,
  recipientAddress,
  metadataJSON,
) => {
  try {
    // Kết nối ví và lấy signer
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();

    // Tạo instance của contract với signer (để ghi dữ liệu)
    const contract = new ethers.Contract(
      CONTRACT_ADDRESS,
      CONTRACT_ABI,
      signer,
    );

    // Parse metadata từ JSON
    const metadata = JSON.parse(metadataJSON);
    console.log("Sử dụng metadata:", metadata);

    // Dùng trực tiếp certificateId làm metadataUrl tạm thời
    // Trong thực tế, cần lưu metadata lên IPFS hoặc máy chủ khác
    const metadataUrl = `https://giacngo.org/api/certificates/metadata/${certificateId}`;

    console.log("Đang gọi mint certificate trên blockchain...");
    console.log({
      recipient: recipientAddress,
      certificateId,
      courseId,
      courseName,
      recipientName,
      metadataUrl,
    });

    try {
      // Gọi hàm mintCertificate trên contract
      const tx = await contract.mintCertificate(
        recipientAddress,
        certificateId,
        courseId,
        courseName,
        recipientName,
        metadataUrl,
      );

      console.log("Transaction submitted:", tx.hash);

      // Đợi transaction được xác nhận
      const receipt = await tx.wait();
      console.log("Transaction confirmed:", receipt);

      // Tìm event CertificateIssued
      // Thêm kiểm tra null/undefined
      const events = receipt.events || [];
      console.log("Events from transaction:", events);

      // Tìm event phù hợp theo tên hoặc theo signature
      const certificateEvent = events.find(
        (e) => e && e.event === "CertificateIssued",
      );

      if (!certificateEvent || !certificateEvent.args) {
        console.error(
          "CertificateIssued event not found in transaction receipt",
        );
        // Fallback nếu không tìm thấy event - trả về kết quả với thông tin hạn chế
        return {
          success: true,
          transactionHash: receipt.transactionHash,
          tokenId: "unknown", // Không có tokenId
          message: "Chứng chỉ NFT đã được tạo, nhưng không thể lấy token ID.",
        };
      }

      const tokenId = certificateEvent.args.tokenId.toString();
      console.log("NFT minted with token ID:", tokenId);

      return {
        success: true,
        transactionHash: receipt.transactionHash,
        tokenId,
        message: "Chứng chỉ NFT đã được tạo thành công!",
      };
    } catch (txError) {
      // Kiểm tra lỗi "Certificate already issued for this course"
      const errorMessage = txError.message || "";
      const errorData = txError.data?.message || "";

      if (
        errorMessage.includes("Certificate already issued for this course") ||
        errorData.includes("Certificate already issued for this course")
      ) {
        console.log("Certificate đã tồn tại cho khóa học này.");

        try {
          // Thử lấy tokenId của certificate đã tồn tại
          // Chúng ta có thể gọi các hàm view của contract để lấy tokenId
          // Nhưng đơn giản hơn, trả về thông báo cho user
          return {
            success: true,
            transactionHash: "", // Không có transaction mới
            tokenId: "already_exists",
            message: "Bạn đã có chứng chỉ NFT cho khóa học này rồi!",
          };
        } catch (viewError) {
          console.error("Không thể truy vấn thông tin certificate:", viewError);
        }
      }

      // Nếu là lỗi khác, ném tiếp lỗi để xử lý chung
      throw txError;
    }
  } catch (error) {
    console.error("Lỗi khi mint NFT Certificate:", error);
    return {
      success: false,
      message: `Lỗi khi tạo chứng chỉ NFT: ${error.message}`,
    };
  }
};

/**
 * Tạo và mint NFT Certificate (quy trình đầy đủ)
 */
export const createAndMintNFTCertificate = async (
  certificate,
  walletAddress,
) => {
  try {
    // Kiểm tra nếu đã kết nối ví
    const { connected, address } = await checkIfWalletConnected();
    if (!connected) {
      throw new Error("Ví chưa được kết nối");
    }

    // Kiểm tra nếu địa chỉ ví trùng khớp
    if (address.toLowerCase() !== walletAddress.toLowerCase()) {
      console.warn(
        `Wallet address mismatch! Connected: ${address}, Provided: ${walletAddress}`,
      );
      // Cập nhật địa chỉ ví cho phù hợp
      walletAddress = address;
    }

    // Tạo metadata cho NFT Certificate
    const metadata = {
      name: `Giác Ngộ Certificate - ${certificate.course_name || "Khóa học Giác Ngộ"}`,
      description: `Certificate của ${certificate.recipient_name} cho khóa học ${certificate.course_name || "Khóa học Giác Ngộ"}`,
      external_url: `https://giacngo.org/certificates/verify/${certificate.certificate_id}`,
      attributes: [
        {
          trait_type: "Course",
          value: certificate.course_name || "Khóa học Giác Ngộ",
        },
        {
          trait_type: "Recipient",
          value: certificate.recipient_name,
        },
        {
          trait_type: "Issue Date",
          value: new Date().toISOString().split("T")[0],
        },
        {
          trait_type: "Certificate ID",
          value: certificate.certificate_id,
        },
      ],
    };

    // Giả lập metadata URL vì API server đang gặp lỗi
    // Thông thường sẽ gọi: const metadataUrl = await saveNFTMetadata(metadata);
    console.log("Đang tạo metadata URL trực tiếp do server lỗi 500...");
    const metadataUrl = `https://giacngo.org/api/certificates/metadata/${certificate.certificate_id}`;

    // Mint NFT Certificate
    const result = await mintNFTCertificate(
      certificate.certificate_id,
      certificate.course_id,
      certificate.course_name || "Khóa học Giác Ngộ",
      certificate.recipient_name,
      walletAddress,
      JSON.stringify(metadata),
    );

    if (result.success) {
      // Đặc biệt xử lý trường hợp certificate đã tồn tại
      if (result.tokenId === "already_exists") {
        console.log("Certificate đã tồn tại, không cần cập nhật database");

        try {
          // Cập nhật thông tin NFT vào database để đánh dấu là đã có NFT
          await updateCertificateWithNFT(
            certificate.certificate_id,
            "already_exists", // tokenId đặc biệt
            "", // Không có transaction hash mới
            true, // isExisting = true
          );

          console.log("Đã cập nhật trạng thái NFT trong database");
        } catch (updateError) {
          console.warn(
            "Không thể cập nhật trạng thái NFT trong database:",
            updateError,
          );
        }

        return {
          success: true,
          message: "Bạn đã có chứng chỉ NFT cho khóa học này rồi!",
          alreadyExists: true,
        };
      }

      // Cập nhật thông tin NFT trong database nếu có tokenId
      if (result.tokenId && result.tokenId !== "unknown") {
        try {
          await updateCertificateWithNFT(
            certificate.certificate_id,
            result.tokenId,
            result.transactionHash,
          );
        } catch (updateError) {
          console.warn(
            "Không thể cập nhật NFT trong database, nhưng NFT đã được tạo thành công:",
            updateError,
          );
          // Không throw lỗi ở đây vì NFT đã được mint thành công
        }
      } else {
        console.warn(
          "Không cập nhật database vì tokenId không xác định. Transaction hash:",
          result.transactionHash,
        );
      }
    }

    return result;
  } catch (error) {
    console.error("Lỗi trong quá trình tạo và mint NFT Certificate:", error);
    return {
      success: false,
      message: `Lỗi: ${error.message}`,
    };
  }
};

/**
 * Xác minh certificate NFT
 */
export const verifyNFTCertificate = async (certificateId, ownerAddress) => {
  try {
    // Kết nối provider read-only
    const provider = getProvider();

    // Tạo instance của contract (chỉ đọc)
    const contract = new ethers.Contract(
      CONTRACT_ADDRESS,
      CONTRACT_ABI,
      provider,
    );

    try {
      // Xác minh quyền sở hữu
      const isValid = await contract.verifyOwnership(
        ownerAddress,
        certificateId,
      );

      if (isValid) {
        // Lấy tokenId từ certificateId
        const tokenId = await contract.getTokenIdByCertificateId(certificateId);

        // Lấy thông tin chi tiết của certificate
        const info = await contract.getCertificateInfo(tokenId);

        return {
          isValid: true,
          certificate: {
            tokenId: tokenId.toString(),
            certificateId: info.certificateId,
            courseId: info.courseId,
            courseName: info.courseName,
            recipientName: info.recipientName,
            issueDate: new Date(info.issueDate.toNumber() * 1000).toISOString(),
            metadataURI: info.metadataURI,
          },
        };
      }

      return {
        isValid: false,
        message: "Chứng chỉ không hợp lệ hoặc không thuộc về địa chỉ ví này",
      };
    } catch (blockchainError) {
      console.log(
        "Không thể xác minh trên blockchain, kiểm tra trong database:",
        blockchainError,
      );

      // Lấy token authentication từ Supabase
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error("Bạn chưa đăng nhập hoặc phiên đăng nhập đã hết hạn");
      }

      // Truy vấn API để kiểm tra certificate trong database
      const response = await fetch(
        `/api/certificates/verify?certificateId=${certificateId}&walletAddress=${ownerAddress}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Lỗi khi xác minh certificate trong database: ${response.statusText}`,
        );
      }

      const data = await response.json();

      // Kiểm tra nếu certificate có nft_token_id = "already_exists"
      if (data.isValid && data.certificate?.nft_token_id === "already_exists") {
        return {
          isValid: true,
          certificate: {
            ...data.certificate,
            tokenId: "already_exists",
            alreadyExists: true,
          },
          message: "Chứng chỉ NFT hợp lệ (đã tồn tại từ trước)",
        };
      }

      return data;
    }
  } catch (error) {
    console.error("Lỗi khi xác minh NFT Certificate:", error);
    return {
      isValid: false,
      message: `Lỗi khi xác minh: ${error.message}`,
    };
  }
};

/**
 * Lấy danh sách tất cả các certificate NFT của một địa chỉ ví
 */
export const getNFTCertificatesForAddress = async (address) => {
  try {
    // Kết nối provider read-only
    const provider = getProvider();

    // Tạo instance của contract
    const contract = new ethers.Contract(
      CONTRACT_ADDRESS,
      CONTRACT_ABI,
      provider,
    );

    // Lấy danh sách sự kiện CertificateIssued cho địa chỉ
    const filter = contract.filters.CertificateIssued(null, address);
    const events = await contract.queryFilter(filter);

    // Lấy thông tin chi tiết của từng certificate
    const certificates = await Promise.all(
      events.map(async (event) => {
        const tokenId = event.args.tokenId;
        const info = await contract.getCertificateInfo(tokenId);

        return {
          tokenId: tokenId.toString(),
          certificateId: info.certificateId,
          courseId: info.courseId,
          courseName: info.courseName,
          recipientName: info.recipientName,
          issueDate: new Date(info.issueDate.toNumber() * 1000).toISOString(),
          metadataURI: info.metadataURI,
          transactionHash: event.transactionHash,
        };
      }),
    );

    return certificates;
  } catch (error) {
    console.error("Lỗi khi lấy danh sách NFT Certificates:", error);
    return [];
  }
};

// Export default để dễ import
export default {
  connectWallet,
  mintNFTCertificate,
  createAndMintNFTCertificate,
  verifyNFTCertificate,
  getNFTCertificatesForAddress,
  checkIfMetaMaskInstalled,
  checkIfWalletConnected,
  getProvider,
  updateCertificateWithNFT,
};
